import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { GoogleMap, useJsApiLoader, MarkerF, Polyline } from '@react-google-maps/api';
import Sidebar from '../components/Sidebar';
import ConfigModal from '../components/ConfigModal';
import { useAuth } from '../contexts/AuthContext';
import '../styles/Map.css';
const configIcon = "https://i.imgur.com/Ez6hzzg.png";

const containerStyle = {
  width: '100%',
  height: '100%'
};

const center = {
  lat: -2.430885,
  lng: -54.727021
};

const mapOptions = {
  disableDefaultUI: true,
  zoomControl: true,
};

const hidePoiStyles = [
  {
    featureType: 'poi',
    stylers: [{ visibility: 'off' }]
  }
];

// Maximum number of points to render in a polyline
const MAX_POLYLINE_POINTS = 100;
// Tempo para reset periódico (2 horas em ms)
const RESET_INTERVAL = 2 * 60 * 60 * 1000;
// Limite máximo de pontos de histórico
const MAX_HISTORICO_POINTS = 500;
// Período de verificação de memória (15 minutos em ms)
const MEMORY_CHECK_INTERVAL = 15 * 60 * 1000;
// Limite de uso de memória em MB para forçar reset
const MEMORY_THRESHOLD_MB = 300;
// Limite de tempo para recriação do componente do mapa (3 horas em ms)
const MAP_RESET_INTERVAL = 3 * 60 * 60 * 1000;
// Intervalo de polling quando a página está visível (1 minuto)
const POLLING_INTERVAL_VISIBLE = 60000;
// Intervalo de polling quando a página não está visível (5 minutos)
const POLLING_INTERVAL_HIDDEN = 300000;

function Map() {
  const location = useLocation();
  const navigate = useNavigate();
  const { empresaId } = location.state || {};
  const { token, user } = useAuth();
  const [empresa, setEmpresa] = useState(null);
  const [funcionarios, setFuncionarios] = useState([]);
  const [markers, setMarkers] = useState([]);
  const [showPoi, setShowPoi] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [historico, setHistorico] = useState([]);
  const [isHistoricoMode, setIsHistoricoMode] = useState(false);
  const [showPolyline, setShowPolyline] = useState(false);
  const [shouldResetMap, setShouldResetMap] = useState(false);
  const [lastMapCenter, setLastMapCenter] = useState(null);
  const [lastMapZoom, setLastMapZoom] = useState(12);
  const [isPageVisible, setIsPageVisible] = useState(document.visibilityState === 'visible');
  const [lastUpdateTime, setLastUpdateTime] = useState(Date.now());
  
  const mapRef = useRef(null);
  const intervalRef = useRef(null);
  const memoryCheckRef = useRef(null);
  const mapResetTimerRef = useRef(null);
  const resetTimerRef = useRef(null);
  
  const apiUrl = process.env.REACT_APP_API_URL;
  
  // Usar useJsApiLoader em vez de LoadScript para melhor performance
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY
  });

  // Compute map styles only when showPoi changes
  const currentMapOptions = useMemo(() => ({
    ...mapOptions,
    styles: showPoi ? [] : hidePoiStyles
  }), [showPoi]);
  
  // Controle de visibilidade da página
  useEffect(() => {
    const handleVisibilityChange = () => {
      const isVisible = document.visibilityState === 'visible';
      const wasVisible = isPageVisible;
      setIsPageVisible(isVisible);
      
      if (isVisible) {
        // Log para debugging
        console.log('Página está visível agora');
        
        // Se a página acabou de ficar visível (transição)
        if (!wasVisible) {
          console.log('Transição para visível - atualizando dados imediatamente');
          // Executar fetchFuncionarios imediatamente na transição
          fetchFuncionarios();
          setLastUpdateTime(Date.now());
        }
        
        // Limpar intervalo anterior se existir
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
        
        // Configurar polling frequente
        console.log('Configurando polling frequente');
        intervalRef.current = setInterval(() => {
          console.log('Executando polling frequente');
          fetchFuncionarios();
          setLastUpdateTime(Date.now());
        }, POLLING_INTERVAL_VISIBLE);
      } else {
        // Página ficou invisível
        console.log('Página em segundo plano');
        
        // Limpar intervalo anterior
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
        
        // Configurar polling menos frequente
        intervalRef.current = setInterval(() => {
          console.log('Executando polling em segundo plano');
          fetchFuncionarios();
          setLastUpdateTime(Date.now());
        }, POLLING_INTERVAL_HIDDEN);
      }
    };
    
    // Configurar listener
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Chamada inicial para configurar o polling corretamente
    // e executar a primeira busca se necessário
    handleVisibilityChange();
    
    // Se a página estiver visível, garantir que temos dados recentes
    if (document.visibilityState === 'visible') {
      console.log('Inicialização com página visível - buscando dados iniciais');
      fetchFuncionarios();
      setLastUpdateTime(Date.now());
    }
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, []);  // Remova lastUpdateTime das dependências
  
  // 1. Salvar estado do mapa periodicamente
  useEffect(() => {
    const mapStateTimer = setInterval(() => {
      if (mapRef.current) {
        const center = mapRef.current.getCenter();
        if (center) {
          setLastMapCenter(center.toJSON());
        }
        setLastMapZoom(mapRef.current.getZoom() || 12);
      }
    }, 10000); // Salvar a cada 10 segundos
    
    return () => {
      clearInterval(mapStateTimer);
    };
  }, []);

  // 2. Função para limpar objetos do Google Maps
  const clearMapObjects = useCallback(() => {
    if (!mapRef.current) return;
    
    try {
      // Forçar limpeza de camadas e listeners
      if (mapRef.current.data) {
        mapRef.current.data.forEach(feature => {
          mapRef.current.data.remove(feature);
        });
      }
      
      // Forçar remoção de listeners de eventos
      // Verificar se o objeto google está disponível na window
      if (window.google && window.google.maps && window.google.maps.event) {
        window.google.maps.event.clearInstanceListeners(mapRef.current);
      }
    } catch (error) {
      console.error('Error cleaning map objects:', error);
    }
  }, []);

  // 3. Reset periódico de componentes para limpar memória acumulada
  useEffect(() => {
    resetTimerRef.current = setTimeout(() => {
      // Salvar estados importantes antes do reset
      const savedState = {
        funcionarios: funcionarios,
        empresa: empresa,
        mapCenter: mapRef.current?.getCenter()?.toJSON() || center,
        mapZoom: mapRef.current?.getZoom() || 12
      };
      
      // Limpar estados grandes
      setMarkers([]);
      setHistorico([]);
      
      // Forçar garbage collection de objetos grandes
      // Aguardar um ciclo para processamento do GC
      setTimeout(() => {
        // Restaurar apenas dados essenciais
        setFuncionarios(savedState.funcionarios);
        setEmpresa(savedState.empresa);
        setLastMapCenter(savedState.mapCenter);
        setLastMapZoom(savedState.mapZoom);
        
        // Recarregar dados frescos
        fetchFuncionarios();
      }, 100);
      
    }, RESET_INTERVAL);
    
    return () => {
      if (resetTimerRef.current) {
        clearTimeout(resetTimerRef.current);
        resetTimerRef.current = null;
      }
    };
  }, []);  // Dependência vazia para executar apenas uma vez na montagem

  // 4. Monitoramento de memória e reset automático
  useEffect(() => {
    const checkMemoryUsage = () => {
      try {
        if (window.performance && 'memory' in window.performance) {
          const estimatedMemory = window.performance.memory.usedJSHeapSize / (1024 * 1024);
          console.log('Memória estimada em uso (MB):', estimatedMemory.toFixed(2));
          
          // Se ultrapassar limite, salvar estado e forçar reload
          if (estimatedMemory > MEMORY_THRESHOLD_MB) {
            console.warn('Uso de memória alto detectado. Recarregando para limpar.');
            
            // Salvar estados importantes no sessionStorage
            sessionStorage.setItem('mapState', JSON.stringify({
              empresaId,
              lastView: {
                center: mapRef.current?.getCenter()?.toJSON() || center,
                zoom: mapRef.current?.getZoom() || 12,
                isHistoricoMode,
                funcionarioId: isHistoricoMode ? markers[0]?.id : null
              }
            }));
            
            // Recarregar página
            window.location.reload();
          }
        }
      } catch (error) {
        console.error('Error checking memory usage:', error);
      }
    };
    
    // Verificar uso de memória periodicamente
    memoryCheckRef.current = setInterval(checkMemoryUsage, MEMORY_CHECK_INTERVAL);
    
    return () => {
      if (memoryCheckRef.current) {
        clearInterval(memoryCheckRef.current);
        memoryCheckRef.current = null;
      }
    };
  }, [empresaId, isHistoricoMode, markers]);

  // 5. Recuperar estado após reload forçado
  useEffect(() => {
    const savedState = sessionStorage.getItem('mapState');
    if (savedState) {
      try {
        const parsedState = JSON.parse(savedState);
        
        // Se temos um estado salvo com o mesmo empresaId
        if (parsedState.empresaId === empresaId) {
          // Restaurar visualização do mapa
          if (parsedState.lastView && mapRef.current) {
            if (parsedState.lastView.center) {
              setLastMapCenter(parsedState.lastView.center);
            }
            
            if (parsedState.lastView.zoom) {
              setLastMapZoom(parsedState.lastView.zoom);
            }
            
            // Se estava em modo histórico, restaurar
            if (parsedState.lastView.isHistoricoMode && parsedState.lastView.funcionarioId) {
              // Iniciar timer para restaurar modo histórico após carregar dados básicos
              setTimeout(() => {
                handleHistorico(parsedState.lastView.funcionarioId);
              }, 1000);
            }
          }
        }
        
        // Limpar storage após uso
        sessionStorage.removeItem('mapState');
      } catch (e) {
        console.error('Erro ao restaurar estado:', e);
        sessionStorage.removeItem('mapState');
      }
    }
  }, [empresaId]);

  // 6. Desmontar e remontar o mapa periodicamente
  useEffect(() => {
    mapResetTimerRef.current = setInterval(() => {
      // Salvar estado atual do mapa
      if (mapRef.current) {
        setLastMapCenter(mapRef.current.getCenter()?.toJSON() || center);
        setLastMapZoom(mapRef.current.getZoom() || 12);
      }
      
      // Desmontar mapa
      setShouldResetMap(true);
      
      // Remontar após breve intervalo
      setTimeout(() => {
        setShouldResetMap(false);
        
        // Limpar referências antigas
        clearMapObjects();
      }, 500);
    }, MAP_RESET_INTERVAL);
    
    return () => {
      if (mapResetTimerRef.current) {
        clearInterval(mapResetTimerRef.current);
        mapResetTimerRef.current = null;
      }
    };
  }, [clearMapObjects]);
  
  // 7. Limitar tamanho do histórico
  useEffect(() => {
    if (historico.length > MAX_HISTORICO_POINTS) {
      setHistorico(current => {
        const step = Math.ceil(current.length / MAX_HISTORICO_POINTS);
        return current.filter((_, index) => 
          index % step === 0 || index === 0 || index === current.length - 1
        );
      });
    }
  }, [historico]);

  // Fetch empresa data
  useEffect(() => {
    if (!empresaId) {
      navigate('/hall');
      return;
    }

    let isMounted = true;
    
    const fetchEmpresa = async () => {
      try {
        const response = await fetch(`${apiUrl}empresas/${empresaId}`);
        if (!isMounted) return;
        if (response.ok) {
          const data = await response.json();
          setEmpresa(data);
        }
      } catch (error) {
        console.error('Error fetching empresa:', error);
      }
    };

    fetchEmpresa();
    
    return () => {
      isMounted = false;
    };
  }, [empresaId, navigate, apiUrl]);

  // Otimizar função para buscar localizações
  const fetchLocalizacoes = useCallback(async (empData) => {
    if (!empData || empData.length === 0) return [];
    
    const localizacoes = await Promise.all(empData.map(async (funcionario) => {
      try {
        const response = await fetch(`${apiUrl}admins/localizacao-atual/${empresaId}/${funcionario.id}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (!response.ok) {
          throw new Error('Failed to fetch location');
        }
        const locationData = await response.json();
        return {
          id: funcionario.id,
          nome: funcionario.nome,
          position: {
            lat: parseFloat(locationData.latitude),
            lng: parseFloat(locationData.longitude)
          }
        };
      } catch (error) {
        console.error(`Error fetching location for funcionario ${funcionario.id}:`, error);
        return null;
      }
    }));
    
    return localizacoes.filter(loc => loc !== null);
  }, [apiUrl, empresaId, token]);

  // Otimizar função para buscar funcionários
  const fetchFuncionarios = useCallback(async () => {
    if (!empresaId || !token) return;
    
    try {
      // // Não fazer requisição se a página estiver invisível (a não ser pelo temporizador)
      // if (!isPageVisible && !intervalRef.current) {
      //   console.log('Ignorando fetchFuncionarios - página não visível');
      //   return;
      // }
      
      console.log('Buscando funcionários e localizações...');
      const response = await fetch(`${apiUrl}admins/empresas/${empresaId}/funcionarios-ativos`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.status === 404) {
        setFuncionarios([]);
        setMarkers([]);
        return;
      }

      const data = await response.json();

      if (!Array.isArray(data)) {
        console.error('Expected an array but got:', data);
        setFuncionarios([]);
        setMarkers([]);
        return;
      }

      setFuncionarios(data);
      
      const localizacoesData = await fetchLocalizacoes(data);
      setMarkers(localizacoesData);
      
      // Atualizar timestamp da última atualização
      setLastUpdateTime(Date.now());
      
    } catch (error) {
      console.error('Error fetching funcionarios:', error);
    }
  }, [empresaId, token, apiUrl, fetchLocalizacoes, isPageVisible]);

  // Otimizar dados do polyline
  const optimizedHistorico = useMemo(() => {
    if (!historico || historico.length <= MAX_POLYLINE_POINTS) return historico;
    
    // Reduzir o número de pontos para históricos grandes
    const step = Math.ceil(historico.length / MAX_POLYLINE_POINTS);
    return historico.filter((_, index) => index % step === 0 || index === historico.length - 1);
  }, [historico]);

  // Path para polyline memoizado
  const polylinePath = useMemo(() => {
    if (!showPolyline || optimizedHistorico.length === 0) return [];
    
    return optimizedHistorico.map(loc => ({ 
      lat: parseFloat(loc.latitude), 
      lng: parseFloat(loc.longitude) 
    }));
  }, [optimizedHistorico, showPolyline]);

  // Handlers memorizados
  const togglePoi = useCallback(() => {
    setShowPoi(prev => !prev);
  }, []);

  const toggleSidebar = useCallback(() => {
    setSidebarOpen(prev => !prev);
  }, []);

  const openModal = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  // Lazy loading para histórico
  const handleHistorico = useCallback(async (userId) => {
    try {
      // Primeiro, limpar histórico anterior para liberar memória
      setHistorico([]);
      
      // Carregar apenas os últimos 500 pontos inicialmente
      const response = await fetch(`${apiUrl}admins/historico-localizacao/${userId}?limit=${MAX_HISTORICO_POINTS}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch historico');
      }

      const data = await response.json();
      
      // Ordenar no servidor se possível, ou aqui de forma eficiente
      const sortedHistorico = data.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
      
      // Aplicar amostragem para conjuntos muito grandes
      let processedHistorico = sortedHistorico;
      if (sortedHistorico.length > MAX_HISTORICO_POINTS) {
        const step = Math.ceil(sortedHistorico.length / MAX_HISTORICO_POINTS);
        processedHistorico = sortedHistorico.filter((_, index) => 
          index % step === 0 || index === 0 || index === sortedHistorico.length - 1
        );
      }
      
      setHistorico(processedHistorico);
      setIsHistoricoMode(true);
      setShowPolyline(true);

      const userMarker = markers.find(marker => marker.id === userId);
      if (userMarker && mapRef.current) {
        mapRef.current.panTo(userMarker.position);
        mapRef.current.setZoom(18);
      }

      // Mostrar apenas o marcador relevante
      setMarkers(currentMarkers => currentMarkers.filter(marker => marker.id === userId));
    } catch (error) {
      console.error('Error fetching historico:', error);
    }
  }, [apiUrl, token, markers]);

  const handleIrParaLocalizacao = useCallback((userId) => {
    const marker = markers.find(marker => marker.id === userId);
    if (marker && mapRef.current) {
      mapRef.current.panTo(marker.position);
      mapRef.current.setZoom(18);
    }
  }, [markers]);

  const exitHistoricoMode = useCallback(() => {
    // Limpar memória explicitamente
    setHistorico([]);
    setShowPolyline(false);
    setIsHistoricoMode(false);
    
    // Recarregar todos os dados
    fetchFuncionarios();
  }, [fetchFuncionarios]);

  const onMapLoad = useCallback(map => {
    mapRef.current = map;
    
    // Se temos uma posição salva do mapa, restaurar
    if (lastMapCenter) {
      map.setCenter(lastMapCenter);
    }
    
    if (lastMapZoom) {
      map.setZoom(lastMapZoom);
    }
  }, [lastMapCenter, lastMapZoom]);
  
  // Cleanup global ao desmontar o componente
  useEffect(() => {
    return () => {
      // Limpar todos os timers e intervalos
      [intervalRef, memoryCheckRef, mapResetTimerRef, resetTimerRef].forEach(ref => {
        if (ref.current) {
          clearInterval(ref.current);
          clearTimeout(ref.current);
          ref.current = null;
        }
      });
      
      // Limpar objetos do mapa
      clearMapObjects();
      
      // Limpar listener de visibilidade
      document.removeEventListener('visibilitychange', () => {});
      
      // Ajuda o GC removendo referências
      mapRef.current = null;
    };
  }, [clearMapObjects]);

  return (
    <div className="map-page">
      <div className="map-container">
        <div className="header">
          <h1>{empresa?.nome}</h1>
          {!isPageVisible && (
            <span style={{ fontSize: '12px', color: '#888', marginLeft: '10px' }}>
              (em segundo plano)
            </span>
          )}
        </div>
        <button onClick={togglePoi} style={{ position: 'absolute', top: '20px', right: '10px', zIndex: 1000 }}>
          {showPoi ? 'Esconder Icones' : 'Mostrar Icones'}
        </button>
        <button onClick={toggleSidebar} style={{ position: 'absolute', top: '17px', left: '20px', zIndex: 1000 }}>
          ☰
        </button>
        <button onClick={openModal} style={{ position: 'absolute', top: '10px', left: '80px', zIndex: 1000, backgroundColor: '#ccc', borderRadius: '50%', padding: '9px', border: 'none', cursor: 'pointer' }}>
          <img src={configIcon} alt="Config" style={{ width: '17px', height: '17px' }} />
        </button>
        
        {/* Renderizar modal apenas quando necessário */}
        {isModalOpen && (
          <ConfigModal isOpen={isModalOpen} onClose={closeModal} empresaId={empresaId} />
        )}
        
        <Sidebar
          users={funcionarios}
          isOpen={sidebarOpen}
          onClose={toggleSidebar}
          onHistorico={handleHistorico}
          onIrParaLocalizacao={handleIrParaLocalizacao}
          empresaId={empresaId}
          solicitanteId={user?.id}
        />
        
        {isHistoricoMode && (
          <div className="historico-modal">
            <button onClick={exitHistoricoMode} className="exit-historico-button">
              Sair do Modo Histórico
            </button>
          </div>
        )}
        
        {/* Renderizar mapa apenas quando carregado e não estiver em reset */}
        {isLoaded && !shouldResetMap && (
          <GoogleMap
            mapContainerStyle={containerStyle}
            center={lastMapCenter || center}
            zoom={lastMapZoom || 12}
            options={currentMapOptions}
            onLoad={onMapLoad}
          >
            {/* Renderizar apenas os marcadores necessários */}
            {markers.map(marker => (
              <MarkerF
                key={marker.id}
                position={marker.position}
                label={{
                  text: marker.nome,
                  fontSize: '14px',
                  fontWeight: 'bold',
                  className: 'marker-label'
                }}
              />
            ))}
            
            {/* Renderizar polyline apenas quando necessário e com dados otimizados */}
            {showPolyline && polylinePath.length > 0 && (
              <Polyline
                path={polylinePath}
                options={{
                  strokeColor: '#FF0000',
                  strokeOpacity: 1.0,
                  strokeWeight: 2,
                }}
              />
            )}
          </GoogleMap>
        )}

        {/* Indicador de atualização */}
        <div style={{ 
          position: 'absolute',
          bottom: '24px',
          left: '5px',  // Modificado de 'right' para 'left'
          backgroundColor: 'rgba(255, 255, 255, 0.7)',
          padding: '5px',
          borderRadius: '3px',
          fontSize: '12px'
        }}>
          Última atualização: {new Date(lastUpdateTime).toLocaleTimeString()}
          {!isPageVisible && <span> (polling reduzido)</span>}
        </div>
      </div>
    </div>
  );
}

export default Map;